import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { ApprovalStatus as Approval_Status_Enum } from '@risksmart-app/domain/src/types/consts/approval-status';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DepartmentPart, TagPart } from 'src/contracts/querySelections';
import {
  getAllContributorsCellValue,
  getAllOwnersCellValue,
} from 'src/rbac/contributorHelper';
import type { LabelledIdArray } from 'src/rbac/types';

import { getFriendlyId } from '@/utils/friendlyId';

import type { AcceptanceFlatFields, AcceptanceTableFields } from './types';

const parentTitleFromLinkedParents = (
  data: AcceptanceFlatFields,
  { riskType }: { [key: string]: string }
) => {
  const titles = data.parents.map((parent) => {
    if (parent.risk) {
      return parent.risk?.Title ? `${parent.risk?.Title} (${riskType})` : '';
    }

    return null;
  });

  return titles.filter((t) => t).join(', ');
};

const parentTierFromLinkedParents = (
  data: AcceptanceFlatFields,
  tiers: { [key: string]: string }
) => {
  return data.parents
    .map((parent) => {
      if (parent.risk) {
        return parent.risk?.Tier
          ? tiers[String(parent.risk?.Tier) as keyof typeof tiers]
          : '';
      }

      return null;
    })
    .filter((t) => t)
    .join(', ');
};

const parentOwnersFromLinkedParents = (data: AcceptanceFlatFields) => {
  const owners: LabelledIdArray = [];
  data.parents.forEach((parent) => {
    if (parent.risk) {
      owners.push(...getAllOwnersCellValue(parent.risk));
    }
  });

  return owners;
};

const parentDepartmentsFromLinkedParents = (data: AcceptanceFlatFields) => {
  const departments: { [departmentTypeId: string]: DepartmentPart } = {};
  data.parents.forEach((parent) => {
    if (parent.risk) {
      for (const departmentType of parent.risk.departments) {
        if (!departments[departmentType.DepartmentTypeId]) {
          departments[departmentType.DepartmentTypeId] = departmentType;
        }
      }
    }
  });

  return Object.values(departments);
};

const parentTagsFromLinkedParents = (data: AcceptanceFlatFields) => {
  const tags: { [tagTypeId: string]: TagPart } = {};
  data.parents.forEach((parent) => {
    if (parent.risk) {
      for (const tagType of parent.risk.tags) {
        if (!tags[tagType.TagTypeId]) {
          tags[tagType.TagTypeId] = tagType;
        }
      }
    }
  });

  return Object.values(tags);
};

const parentContributorsFromLinkedParents = (data: AcceptanceFlatFields) => {
  const contributors: LabelledIdArray = [];
  data.parents.forEach((parent) => {
    if (parent.risk) {
      contributors.push(...getAllContributorsCellValue(parent.risk));
    }
  });

  return contributors;
};

export const useLabelledFields = (
  records: AcceptanceFlatFields[] | undefined
) => {
  const { getLabel } = useRating('acceptance_status');
  const { t } = useTranslation(['common']);
  const tiers = useMemo(() => t('tiers', { returnObjects: true }), [t]);

  return useMemo<AcceptanceTableFields[] | undefined>(() => {
    const typeLabels = { riskType: t('risk') };

    return records?.map((d) => {
      const pendingChangeRequests = d.changeRequests.filter(
        (cr) => cr.ChangeRequestStatus === Approval_Status_Enum.Pending
      );
      // `Type` is CHECK-constrained to create | update | delete. Matching the
      // literal mirrors `useChangeRequests`, which drives the detail page's
      // DeleteRequestAlert banner off the same discriminator.
      const hasPendingDeleteRequest = pendingChangeRequests.some(
        (cr) => cr.Type === 'delete'
      );
      const status =
        pendingChangeRequests.length > 0 ? 'pending_approval' : d.Status;

      return {
        ...d,
        Status: status,
        // Surfaced as its own field rather than folded into `StatusLabelled`,
        // which has to stay a pure function of `Status`: the dashboard widget
        // groups acceptances by `Status` but labels the bucket and builds its
        // clickthrough filter from `StatusLabelled`
        // (`dashboards/universal-widget/data-sources/acceptance.ts`), so a label
        // that varied independently would give one bucket two names and a
        // clickthrough that selects only part of it. The register renders the
        // pending-deletion indication in the status cell instead (COI-916).
        hasPendingDeleteRequest,
        StatusLabelled: status ? getLabel(status) : '-',
        ParentTitle: parentTitleFromLinkedParents(d, typeLabels) ?? '-',
        Tier: parentTierFromLinkedParents(d, tiers) ?? '-',
        ModifiedByUserName: d.modifiedByUser?.FriendlyName ?? null,
        allOwners: parentOwnersFromLinkedParents(d),
        allContributors: parentContributorsFromLinkedParents(d),
        requestedBy:
          d.requestedByUser?.FriendlyName ??
          d.requestedByUserGroup?.Name ??
          null,
        approvedBy:
          d.approvedByUser?.FriendlyName ?? d.approvedByUserGroup?.Name ?? null,
        SequentialIdLabel: getFriendlyId(
          Parent_Type_Enum.Acceptance,
          d.SequentialId
        ),
        departments: parentDepartmentsFromLinkedParents(d),
        tags: parentTagsFromLinkedParents(d),
      };
    });
  }, [getLabel, records, tiers, t]);
};
